import { z } from "zod"
import { medicationTakenAnswers } from "@/model/BloodGlucoseForm"
import { quickCheckReasons } from "@/model/QuickCheckReason"

const baseFields = {
  bloodGlucoseInMilligramsPerDeciliter: z.number().int().min(20).max(800),
  dateTime: z.string(), // ISO with offset
  notes: z.string().max(2000).optional(),
}

const mealContexts = [
  "breakfast",
  "lunch",
  "dinner",
  "snack-before-breakfast",
  "snack-between-breakfast-and-lunch",
  "snack-between-lunch-and-dinner",
  "snack-after-dinner",
] as const

export type MealContext = (typeof mealContexts)[number]

export const fastingMeasurementSchema = z.object({
  ...baseFields,
})

// Empty number inputs produce NaN via valueAsNumber; treat that as absent.
const optionalGrams = z.number().min(0).max(1000).optional().catch(undefined)

export const mealMeasurementSchema = z.object({
  ...baseFields,
  context: z.enum(mealContexts),
  mealContents: z.string().optional(),
  carbsInGrams: optionalGrams,
  proteinInGrams: optionalGrams,
  medicationTaken: z.enum(medicationTakenAnswers).nullable().optional(),
  medicationNote: z.string().max(2000).optional(),
})

export const foodLogSchema = z.object({
  context: z.enum(mealContexts),
  dateTime: z.string(),
  mealContents: z.string().min(1, "Tell us what you ate"),
  carbsInGrams: optionalGrams,
  proteinInGrams: optionalGrams,
  notes: z.string().max(2000).optional(),
})

export const quickCheckSchema = z.object({
  ...baseFields,
  reasons: z
    .array(z.enum(quickCheckReasons))
    .min(1, "Select at least one reason"),
})

export type FastingMeasurementFormData = z.infer<typeof fastingMeasurementSchema>
export type MealMeasurementFormData = z.infer<typeof mealMeasurementSchema>
export type FoodLogFormData = z.infer<typeof foodLogSchema>
export type QuickCheckFormData = z.infer<typeof quickCheckSchema>
